import dynamic, { type DynamicOptions, type Loader } from 'next/dynamic';
import type { ComponentType } from 'react';
import { ErrorPopup } from './ErrorPopup';

type DynamicWrapperOptions<P> = DynamicOptions<P> & {
  componentName?: string;
  retryAttempts?: number;
};

export function dynamicWithRetry<P = object>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  options: DynamicWrapperOptions<P> = {},
) {
  const {
    componentName = 'UnknownDynamicComponent',
    retryAttempts = 2,
    loading,
    ...rest
  } = options;

  const errorHandler = (error: Error, message: string, attempt?: number) => {
    if (rest.ssr || rest.ssr === undefined) {
      import('@repo/logger').then(mod => {
        const { loggerFactory } = mod;
        const logger = loggerFactory({
          path: '@repo/ui/DynamicWithRetry/index.ts',
          component: 'dynamicWithRetry',
        });
        logger.error({ attempt, componentName, error }, message);
      });
    } else {
      import('@datadog/browser-rum').then(mod => {
        const { datadogRum } = mod;
        datadogRum.addError(error, {
          componentName,
          attempt,
          message,
        });
      });
    }
  };

  const loader: Loader<P> = () => {
    let attempt = 0;

    const tryImport = (): Promise<{ default: ComponentType<P> }> =>
      importFn().catch(error => {
        attempt++;

        if (attempt <= retryAttempts) {
          return new Promise(resolve => {
            setTimeout(() => {
              resolve(tryImport());
            }, 1000);
          });
        }

        errorHandler(error, '[dynamicWithRetry] Final attempt failed', attempt);

        return Promise.resolve({
          default: () => <ErrorPopup />,
        });
      });

    return tryImport();
  };

  return dynamic<P>(loader, {
    ...rest,
    // Returning loading as null would override the pre rendered HTML causing the component to flicker
    ...(loading && {
      loading: props => {
        if (props.error) {
          errorHandler(props.error, '[dynamicWithRetry] Chunk load failed in loading fallback');
        }

        return loading(props);
      },
    }),
  });
}
